/**
 * Écran des arbitrages CGIA.
 *
 * Les décisions métier de l'analyse (tiret dans un nom composé, contrôle
 * bloquant ou non, source d'adresse prioritaire) sont parties deux fois par
 * mail et deux fois restées sans réponse : sept questions imbriquées dans un
 * mail ne se répondent pas depuis un téléphone entre deux dossiers.
 *
 * D'où la forme : une question par écran, le contexte et les dossiers d'origine
 * sous les yeux, la réponse en un geste, le commentaire libre facultatif, et la
 * progression enregistrée à chaque réponse pour qu'on reprenne où on s'est
 * arrêté.
 */
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EditIcon from '@mui/icons-material/Edit';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  LinearProgress,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';

import { makeAuthenticatedApiCall } from '../../apiHelper';

interface ArbitrationOption {
  key: string;
  label: string;
  detail?: string;
}

interface ArbitrationSourceRef {
  plate: string;
  note?: string;
}

interface Arbitration {
  id: number;
  arbitrationKey: string;
  category: string;
  question: string;
  context: string | null;
  sourceRefs: ArbitrationSourceRef[];
  options: ArbitrationOption[];
  feedbackCount: number;
  status: 'open' | 'answered';
  answerKey: string | null;
  answerText: string | null;
  answeredBy: string | null;
  answeredAt: string | null;
}

interface ArbitrationsResponse {
  success: boolean;
  data: Arbitration[];
  progress: { answered: number; total: number };
}

const CATEGORY_LABELS: Record<string, string> = {
  identite: 'Identité',
  adresse: 'Adresse',
  controles: 'Contrôles',
  pieces: 'Pièces',
  affichage: 'Affichage',
};

const ArbitragesCgiaPage = () => {
  const queryClient = useQueryClient();
  const [index, setIndex] = useState(0);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [comment, setComment] = useState('');
  const [justSaved, setJustSaved] = useState(false);

  const { data, isLoading, isError } = useQuery<ArbitrationsResponse>({
    queryKey: ['cgiaArbitrations'],
    queryFn: async () => {
      const response = await makeAuthenticatedApiCall<ArbitrationsResponse>('get', '/api/cgia-arbitrations');
      return response.data;
    },
  });

  const arbitrations = useMemo(() => data?.data ?? [], [data]);
  const current = arbitrations[index];

  // Le formulaire suit la question affichée : sans ça, la réponse d'une
  // question resterait cochée sur la suivante.
  useEffect(() => {
    setSelectedKey(current?.answerKey ?? null);
    setComment(current?.answerText ?? '');
    setJustSaved(false);
  }, [current?.id, current?.answerKey, current?.answerText]);

  const answerMutation = useMutation({
    mutationFn: async (payload: { id: number; answerKey: string | null; answerText: string | null }) => {
      const response = await makeAuthenticatedApiCall('post', `/api/cgia-arbitrations/${payload.id}/answer`, {
        answerKey: payload.answerKey,
        answerText: payload.answerText,
      });
      return response.data;
    },
    onSuccess: async () => {
      setJustSaved(true);
      await queryClient.invalidateQueries({ queryKey: ['cgiaArbitrations'] });
    },
  });

  if (isLoading) {
    return (
      <Box sx={{ p: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
        <CircularProgress size={20} />
        <Typography>Chargement des arbitrages…</Typography>
      </Box>
    );
  }

  if (isError) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Impossible de charger les arbitrages.</Alert>
      </Box>
    );
  }

  if (arbitrations.length === 0) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">Aucun arbitrage en attente.</Alert>
      </Box>
    );
  }

  const progress = data?.progress ?? { answered: 0, total: arbitrations.length };
  const isOpenQuestion = current.options.length === 0;
  const canSave = isOpenQuestion ? comment.trim().length > 0 : selectedKey !== null;

  const goTo = (nextIndex: number) => {
    setIndex(Math.max(0, Math.min(arbitrations.length - 1, nextIndex)));
  };

  const handleSave = () => {
    answerMutation.mutate({
      id: current.id,
      answerKey: isOpenQuestion ? null : selectedKey,
      answerText: comment.trim().length > 0 ? comment.trim() : null,
    });
  };

  return (
    <Box sx={{ maxWidth: 720, mx: 'auto', p: { xs: 2, sm: 3 }, pb: 10 }}>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
        Arbitrages analyse CGIA
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Des décisions que le code ne peut pas prendre à ta place. Une par écran, tu peux t’arrêter et reprendre quand tu
        veux.
      </Typography>

      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
        <LinearProgress
          variant="determinate"
          value={(progress.answered / Math.max(1, progress.total)) * 100}
          sx={{ flex: 1, height: 8, borderRadius: 4 }}
        />
        <Typography variant="body2" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
          {progress.answered} / {progress.total}
        </Typography>
      </Stack>

      <Paper elevation={0} sx={{ p: { xs: 2, sm: 3 }, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
        <Stack direction="row" spacing={1} sx={{ mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
          <Chip size="small" label={CATEGORY_LABELS[current.category] ?? current.category} />
          {current.feedbackCount > 1 && (
            <Chip size="small" color="warning" label={`${current.feedbackCount} retours concernés`} />
          )}
          {current.status === 'answered' && (
            <Chip size="small" color="success" icon={<CheckCircleIcon />} label="Répondu" />
          )}
        </Stack>

        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>
          {current.question}
        </Typography>

        {current.context && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, whiteSpace: 'pre-line' }}>
            {current.context}
          </Typography>
        )}

        {current.sourceRefs.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
              Dossiers concernés
            </Typography>
            <Stack spacing={0.5} sx={{ mt: 0.5 }}>
              {current.sourceRefs.map((ref) => (
                <Typography key={ref.plate} variant="body2">
                  <strong>{ref.plate}</strong>
                  {ref.note ? ` — ${ref.note}` : ''}
                </Typography>
              ))}
            </Stack>
          </Box>
        )}

        <Divider sx={{ my: 2 }} />

        {isOpenQuestion ? (
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
            Ta réponse
          </Typography>
        ) : (
          <Stack spacing={1.5} sx={{ mb: 2 }}>
            {current.options.map((option) => {
              const selected = selectedKey === option.key;
              return (
                <Button
                  key={option.key}
                  onClick={() => setSelectedKey(option.key)}
                  variant={selected ? 'contained' : 'outlined'}
                  sx={{
                    justifyContent: 'flex-start',
                    textAlign: 'left',
                    textTransform: 'none',
                    py: 1.5,
                    px: 2,
                    borderRadius: 2,
                  }}
                  fullWidth
                >
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {option.label}
                    </Typography>
                    {option.detail && (
                      <Typography variant="body2" sx={{ opacity: 0.85 }}>
                        {option.detail}
                      </Typography>
                    )}
                  </Box>
                </Button>
              );
            })}
          </Stack>
        )}

        <TextField
          value={comment}
          onChange={(event) => setComment(event.target.value)}
          label={isOpenQuestion ? 'Ta réponse' : 'Précision (facultatif)'}
          placeholder={isOpenQuestion ? 'Explique-moi le cas' : 'Une nuance, une exception, une liste…'}
          multiline
          minRows={isOpenQuestion ? 3 : 2}
          fullWidth
        />

        {answerMutation.isError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {(answerMutation.error as any)?.response?.data?.error ?? 'Enregistrement impossible'}
          </Alert>
        )}
        {justSaved && !answerMutation.isPending && (
          <Alert severity="success" sx={{ mt: 2 }} icon={<CheckCircleIcon fontSize="inherit" />}>
            Réponse enregistrée.
          </Alert>
        )}

        <Button
          variant="contained"
          size="large"
          onClick={handleSave}
          disabled={!canSave || answerMutation.isPending}
          sx={{ mt: 2, textTransform: 'none', fontWeight: 700 }}
          fullWidth
          startIcon={current.status === 'answered' ? <EditIcon /> : undefined}
        >
          {answerMutation.isPending
            ? 'Enregistrement…'
            : current.status === 'answered'
              ? 'Modifier ma réponse'
              : 'Valider'}
        </Button>
      </Paper>

      <Stack direction="row" justifyContent="space-between" sx={{ mt: 2 }}>
        <Button
          onClick={() => goTo(index - 1)}
          disabled={index === 0}
          startIcon={<ArrowBackIcon />}
          sx={{ textTransform: 'none' }}
        >
          Précédent
        </Button>
        <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'center' }}>
          {index + 1} / {arbitrations.length}
        </Typography>
        <Button
          onClick={() => goTo(index + 1)}
          disabled={index === arbitrations.length - 1}
          endIcon={<ArrowForwardIcon />}
          sx={{ textTransform: 'none' }}
        >
          {current.status === 'answered' ? 'Suivant' : 'Passer'}
        </Button>
      </Stack>
    </Box>
  );
};

export default ArbitragesCgiaPage;
